import { ICompileService, IOnInit } from "angular";
import { numberFormatterFactory } from "../../../../records/csv/formatters";
import { RecordDataSource } from "../../../../records/grids";
import {
  decimalValueGetterFactory,
  lookupValueGetterFactory,
  moneyValueGetterFactory,
  percentageValueGetterFactory,
} from "../../../../records/grids/cells/valueGetters";
import { ColDef } from "../../../../records/grids/columns/colDef";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../../records/grids/columns/columnDefinitionService";
import { FilterProvider } from "../../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../../records/grids/columns/filters/subFilters/LookupFilter";
import { OptionsFilter } from "../../../../records/grids/columns/filters/subFilters/OptionsFilter";
import { IRowCommandEventArgs } from "../../../../records/grids/controller";
import { IGridSettings } from "../../../../records/grids/settings/gridSettingsService";
import { IQuery } from "../../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import { MachineConfigurationView } from "../../models/machineConfiguration";
import { machineConfigurationCellClass } from "../machineConfigurationCellClass";

const getUrl = (path?: string) =>
  path === undefined ? `/api/machines/Machine` : `/api/machines/${path}`;

export class MachineConfigurationGridComponentController implements IOnInit {
  static $inject = [
    ServerUtilityName,
    ColumnDefinitionServiceName,
    "$compile",
    "environment",
  ];

  gridDataSource!: RecordDataSource;
  columns!: ColDef[];

  /* bindings */
  readonly gridSettings!: IGridSettings;
  readonly onItemCommand: (e: {
    args: IRowCommandEventArgs<MachineConfigurationView>;
  }) => void = () => {};
  readonly gridSelectionChanged: (e: {
    data: any;
    selected: boolean;
    gridSelection: any[];
  }) => void = () => {};
  readonly showSelectButtons!: boolean;
  readonly defaultQuery!: IQuery;
  readonly decorateCurrentAndFutureConfigurations!: boolean;
  readonly additionalColumns: ColDef[] = [];
  /* bindings */

  constructor(
    private readonly serverUtility: ServerUtility,
    private readonly columnDefinitionService: ColumnDefinitionService,
    private readonly $compile: ICompileService,
    private readonly environment: { Date: Date }
  ) {}

  $onInit(): void {
    this.gridDataSource = new RecordDataSource(
      this.serverUtility,
      "/api/machines/MachineConfiguration"
    );
    this.columns = this.getColumns(
      this.serverUtility,
      this.columnDefinitionService,
      this.$compile,
      this.additionalColumns
    );
  }

  getColumns(
    serverUtility: ServerUtility,
    columnDefinitionService: ColumnDefinitionService,
    $compile: ICompileService,
    additionalColumns: ColDef[]
  ): ColDef[] {
    const columns: ColDef[] = [
      columnDefinitionService.getNumberColDef({
        field: "Id",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "SerialNumber",
        header: "Serial Number",
        filter: new FilterProvider("SerialNumber", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineId"],
              serverQuery: (search) =>
                serverUtility.query({
                  url: getUrl(),
                  query: {
                    SerialNumberContains: search,
                    Take: 10,
                    OrderBy: "SerialNumber",
                  },
                }),
              displayField: "SerialNumber",
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getDateColDef({
        field: "ManufacturersDate",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineManufacturerName",
        header: "Manufacturer",
        filter: new FilterProvider("MachineManufacturerName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineManufacturerId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("MachineManufacturer"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineModelTypeName",
        header: "Model Type",
        filter: new FilterProvider("MachineModelTypeName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineModelTypeId"],
              serverQuery: (search) =>
                serverUtility
                  .lookupQuery({
                    url: getUrl("MachineModelType"),
                    search,
                  })
                  .then((result) => {
                    return result;
                  }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineModelName",
        header: "Model",
        filter: new FilterProvider("MachineModelName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineModelId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("MachineModel"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getNumberColDef({ field: "Order", hide: true }),
      columnDefinitionService.getStringColDef({
        field: "ChangeType",
        hide: true,
        valueGetter: lookupValueGetterFactory({
          lookup: {
            NewMachine: "New Machine",
          },
        }),
        filter: new FilterProvider("ChangeType", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              queryFieldNames: ["ChangeType"],
              options: [
                { display: "Decommission", value: "Decommission" },
                { display: "Conversion", value: "Conversion" },
                { display: "Move", value: "Move" },
                { display: "New Machine", value: "NewMachine" },
                { display: "Warehouse", value: "Warehouse" },
              ],
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "Status",
        filter: new FilterProvider("Status", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              options: [
                { display: "Floor", value: "Floor" },
                { display: "Warehoused", value: "Warehoused" },
                { display: "Decommissioned", value: "Decommissioned" },
              ],
              queryFieldNames: ["Status"],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getBooleanColDef({
        field: "CurrentConfiguration",
        hide: true,
      }),
      columnDefinitionService.getDateColDef({
        field: "InServiceDate",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "SlotNumber",
        header: "Asset Number",
      }),
      columnDefinitionService.getStringColDef({ field: "Notes", hide: true }),
      columnDefinitionService.getDateColDef({
        field: "Date",
        header: "Project Date",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ProjectNumber",
        header: "Project Number",
      }),
      columnDefinitionService.getStringColDef({
        field: "PropertyName",
        header: "Property",
        filter: new FilterProvider("PropertyName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["PropertyId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({ url: "/api/properties", search }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineAreaName",
        header: "Area",
        hide: true,
        filter: new FilterProvider("MachineAreaName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineAreaId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("MachineArea"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineZoneName",
        header: "Zone",
        hide: true,
        filter: new FilterProvider("MachineZoneName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineZoneId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("MachineZone"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineBankName",
        header: "Bank",
        hide: true,
        filter: new FilterProvider("MachineBankName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineBankId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("MachineBank"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "Position",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "Location",
      }),
      columnDefinitionService.getNumberColDef({
        field: "BillValidatorCapacity",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineGameCategoryName",
        hide: true,
        header: "Game Category",
        filter: new FilterProvider("MachineGameCategoryName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineGameCategoryId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("MachineGameCategory"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "JackpotLimit",
        valueGetter: moneyValueGetterFactory(),
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "CreditLimit",
        valueGetter: moneyValueGetterFactory(),
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "PrinterLimit",
        valueGetter: moneyValueGetterFactory(),
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "BillLimit",
        valueGetter: moneyValueGetterFactory(),
        hide: true,
      }),
      columnDefinitionService.getStringColDef({ field: "Topper", hide: true }),
      columnDefinitionService.getBooleanColDef({
        field: "Roulette",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "RouletteId",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "MeterDelta",
        hide: true,
      }),
      columnDefinitionService.getBooleanColDef({
        field: "TournamentEnabled",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDefinitionName",
        filter: new FilterProvider("ProgressiveDefinitionName", $compile)
          .defaultStringFilters()
          .addNullFilter("ProgressiveDefinitionId")
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ProgressiveDefinitionId"],
              serverQuery: (search) =>
                this.serverUtility.lookupQuery({
                  url: "/api/machines/progressives/ProgressiveDefinition",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveTypeName",
        filter: new FilterProvider("ProgressiveTypeName", $compile)
          .defaultStringFilters()
          .addNullFilter("ProgressiveTypeId")
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ProgressiveTypeId"],
              serverQuery: (search) =>
                this.serverUtility.lookupQuery({
                  url: "/api/machines/progressives/ProgressiveType",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getBooleanColDef({
        field: "LeasedSoftware",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "DailyFee",
        hide: true,
        valueGetter: moneyValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "CoinInPercentage",
        hide: true,
        valueGetter: percentageValueGetterFactory(),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MinDaysNotification",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "IViewVersionName",
        header: "iView Version",
        filter: new FilterProvider("IViewVersionName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["IViewVersionId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("IViewVersion"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "IViewContent",
        header: "iView Content",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "IViewOSVersion",
        header: "iView OS Version",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "NXPFirmware",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "GenesisFirmware",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "GMUDOCID",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ROMSIGSoftwareName",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "PrinterManufacturer",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "PrinterModel",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "PrinterSerialNumber",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "BillValidatorManufacturer",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "BillValidatorModel",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "BVSerialNumber",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "MainDoorMD5_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "MainDoorMD1_1_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({ field: "CPU5_8", hide: true }),
      columnDefinitionService.getNumberColDef({
        field: "CPU1_1_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "BellyDoorBVS5_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "BellyDoorBVS1_1_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "CashboxBVC5_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "CashboxBVC1_1_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "CashBoxDoorBVA5_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "CashBoxDoorBVA1_1_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "ElectricalBaseELE5_8",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "ElectricalBaseELE1_1_8",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "AccountingMachineDenominationValue",
        header: "Accounting Denomination",
        hide: true,
        valueGetter: (params) =>
          (<MachineConfigurationView>params.data)
            .AccountingMachineDenominationName,
        filter: new FilterProvider(
          "AccountingMachineDenominationValue",
          $compile
        )
          .defaultNumberFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["AccountingDenominationId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("MachineDenomination"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "BaseDenomValue",
        header: "Base Denom",
        hide: true,
        valueGetter: (params) =>
          (<MachineConfigurationView>params.data).BaseDenom,
        filter: new FilterProvider("BaseDenomValue", $compile)
          .defaultNumberFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["BaseMachineDenominationId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("MachineDenomination"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "PrimaryTheme",
        header: "Theme",
        hide: true,
        filter: new FilterProvider("PrimaryTheme", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["PrimaryMachineThemeId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: getUrl("MachineTheme"),
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getBooleanColDef({
        field: "MultiTheme",
        header: "Multi Theme",
        hide: true,
      }),
      columnDefinitionService.getBooleanColDef({
        field: "MultiDenom",
        header: "Multi Denom",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "MinPaybackPercentage",
        header: "Min Payback %",
        hide: true,
        valueGetter: percentageValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MaxPaybackPercentage",
        header: "Max Payback %",
        hide: true,
        valueGetter: percentageValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "MaxPaybackWithContribution",
        header: "Max Payback with PC",
        hide: true,
        valueGetter: percentageValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "HoldPercentage",
        header: "Hold %",
        hide: true,
        valueGetter: percentageValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getBooleanColDef({
        field: "VariableHoldPercentage",
        header: "Variable Hold %",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "MaxBet",
        header: "Max Bet",
        hide: true,
        valueGetter: decimalValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "TopCreditsAward",
        header: "Top Award (Credits)",
        hide: true,
        valueGetter: decimalValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getNumberColDef({
        field: "TopDollarAward",
        header: "Top Award ($)",
        hide: true,
        valueGetter: moneyValueGetterFactory({ decimalPlaces: 2 }),
      }),
      columnDefinitionService.getStringColDef({
        field: "PaytableId",
        header: "Paytable ID",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "Paylines",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "Ownership",
        hide: true,
        filter: new FilterProvider("Ownership", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              queryFieldNames: ["Ownership"],
              options: [
                { display: "Owned", value: "Owned" },
                { display: "Leased", value: "Leased" },
              ],
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "ContractNumber",
        hide: true,
      }),
      columnDefinitionService.getDateColDef({
        field: "ContractExpiryDate",
        hide: true,
      }),
    ];

    columns.splice(0, 0, ...additionalColumns);

    if (this.decorateCurrentAndFutureConfigurations) {
      const cellDecorator = machineConfigurationCellClass(
        this.environment.Date,
        "CurrentConfiguration",
        "Date"
      );
      columns.forEach((c: ColDef) => {
        c.cellClass = cellDecorator;
      });
    }

    return columns;
  }
}
